import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CreditText,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  EmailText,
  HeaderContainer,
  HeaderProfilePhoto,
  HeaderProfilePhotoSkeleton,
  HeaderRightSide,
  HeaderWrapper,
  LogoWrapper,
  NameColumn,
  PauseIcon,
  PlayIcon,
  ProfileSection,
  ProUpgradeButtonContainer,
  ProUpgradeButtonSkeleton,
  Row,
} from "./Header.styles";
import { logout } from "../../store/slices/auth/asyncThunks";
import {
  selectAuthLoading,
  selectLoggedIn,
  setAuthLoading,
} from "../../store/slices/auth/slice";
import { selectUser } from "../../store/slices/user/slice";
import {
  selectAutoPlay,
  selectSignUpModalOpen,
  setAffiliateRef,
  setAutoPlay,
  setSignUpModalOpen,
  setSource,
} from "../../store/slices/common/slice";
import Logo from "../Logo/Logo";
import { ReactComponent as StarSvg } from "../../assets/icons/star.svg";
import { ReactComponent as WhiteStarSvg } from "../../assets/icons/whiteStarOutline.svg";
import { ReactComponent as LogoutSvg } from "../../assets/icons/logout.svg";
import { ReactComponent as ParticlesSvg } from "../../assets/icons/whiteParticles.svg";
import { ReactComponent as RewardsSvg } from "../../assets/icons/award.svg";
import { ReactComponent as WalletSvg } from "../../assets/icons/wallet.svg";

import { ReactComponent as ProfileSvg } from "../../assets/icons/profile.svg";
import { ReactComponent as AffiliateSvg } from "../../assets/icons/affiliate.svg";
import { ReactComponent as EditSvg } from "../../assets/icons/edit.svg";
import DefaultProfilePhoto from "../DefaultProfilePhoto/DefaultProfilePhoto";
import AuthModal from "../AuthModal/AuthModal";
import FreeCreditsModal from "../FreeCreditsModal/FreeCreditsModal";
import UsernameModal from "../UsernameModal/UsernameModal";
import { priceFormatter } from "../../utils/utils";
import { toast } from "sonner";
import {
  clearExploreCollections,
  selectCategories,
  setSelectedExploreCollectionFilter,
} from "../../store/slices/model/slice";

const Header = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownAnimating, setIsDropdownAnimating] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUsernameModalOpen, setIsUsernameModalOpen] = useState(false);
  const [isFreeCreditsModalOpen, setIsFreeCreditsModalOpen] = useState(false);
  const navigate = useNavigate();
  const loggedIn = useSelector(selectLoggedIn);
  const authLoading = useSelector(selectAuthLoading);
  const user = useSelector(selectUser);
  const dropdownRef = useRef(null);
  const signUpModalOpen = useSelector(selectSignUpModalOpen);
  const autoplayEnabled = useSelector(selectAutoPlay);
  const { data: categories } = useSelector(selectCategories);

  const handleAutoplayToggle = () => {
    localStorage.setItem("autoPlay", !autoplayEnabled);
    dispatch(setAutoPlay(!autoplayEnabled));
  };

  const closeDropdown = () => {
    setIsDropdownAnimating(true);
    setTimeout(() => {
      setIsDropdownOpen(false);
      setIsDropdownAnimating(false);
    }, 150);
  };

  useEffect(() => {
    if (user) {
      if (user.username.includes("-")) {
        setIsUsernameModalOpen(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (signUpModalOpen) {
      setIsLoginModalOpen(true);
    }
  }, [signUpModalOpen]);

  useEffect(() => {
    if (searchParams.get("code")) {
      setIsLoginModalOpen(true);
    }
    const ref = searchParams.get("ref");
    const source = searchParams.get("source");

    const savedAffiliateRef = localStorage.getItem("affiliateRef");
    const savedDateAffiliateRef = localStorage.getItem("dateAffiliateRef");
    const savedSource = localStorage.getItem("source");
    const savedDateSource = localStorage.getItem("dateSource");
    let hasStoredRef = false;
    let hasStoredSource = false;

    if (
      savedAffiliateRef &&
      savedDateAffiliateRef &&
      new Date(savedDateAffiliateRef).getTime() >
        new Date().getTime() - 2 * 24 * 60 * 60 * 1000
    ) {
      dispatch(setAffiliateRef(savedAffiliateRef));
      hasStoredRef = true;
    }

    if (
      savedSource &&
      savedDateSource &&
      new Date(savedDateSource).getTime() >
        new Date().getTime() - 2 * 24 * 60 * 60 * 1000
    ) {
      dispatch(setSource(savedSource));
      hasStoredSource = true;
    }

    if (ref) {
      if (loggedIn) {
        searchParams.delete("ref");
        setSearchParams(searchParams);
      } else {
        if (!hasStoredRef) {
          localStorage.setItem("affiliateRef", ref);
          localStorage.setItem("dateAffiliateRef", new Date().toISOString());
          dispatch(setAffiliateRef(ref));
        }
      }
    }

    if (source) {
      if (loggedIn) {
        searchParams.delete("source");
        setSearchParams(searchParams);
      } else {
        if (!hasStoredSource) {
          localStorage.setItem("source", source);
          localStorage.setItem("dateSource", new Date().toISOString());
          dispatch(setSource(source));
        }
      }
    }
  }, [searchParams, dispatch, loggedIn, setSearchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    if (loggedIn) {
      if (isDropdownOpen) {
        closeDropdown();
      } else {
        setIsDropdownOpen(!isDropdownOpen);
      }
    } else {
      setIsLoginModalOpen(!isLoginModalOpen);
    }
  };

  const handleNavigate = (path) => {
    navigate(`/${path}`);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }, 300);
  };
  const handleUpgradeToPro = () => {
    if (loggedIn) {
      handleNavigate("subscription");
    } else {
      setIsLoginModalOpen(!isLoginModalOpen);
    }
  };

  const handleDropdownItemClick = (action) => {
    if (action === "autoPlay") {
      handleAutoplayToggle();
    } else if (action === "subscriptions") {
      handleNavigate("subscription");
    } else if (action === "freeCredits") {
      //   setIsFreeCreditsModalOpen(true);
      handleNavigate("spin");
    } else if (action === "deleteAccount") {
      handleNavigate("account/delete");
    } else if (action === "logout") {
      toast.success("Successfully logged out!");
      dispatch(logout())
        .unwrap()
        .then(() => {})
        .catch(() => {})
        .finally(() => {
          dispatch(setAuthLoading(false));
        });
    } else if (action === "templateRewards") {
      handleNavigate("rewards/template");
    } else if (action === "affiliate") {
      handleNavigate("referral/earnings");
    } else if (action === "wallet") {
      handleNavigate("wallet");
    }
    closeDropdown();
  };

  const handleFreeCreditsModalClose = () => {
    setIsFreeCreditsModalOpen(false);
  };

  // Only skeleton while auth is still being determined. Once we know the user
  // is logged OUT (loggedIn === false), show the default avatar instead of an
  // endless skeleton.
  const isProfilePhotoLoading = authLoading && loggedIn !== false;
  const shouldOpenUsernameModalFromProfileRow =
    Number(user?.activeModels ?? 0) === 0;

  const handleProfileRowClick = () => {
    if (shouldOpenUsernameModalFromProfileRow) {
      setIsUsernameModalOpen(true);
      closeDropdown();
      return;
    }

    navigate("/profile");
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
    closeDropdown();
  };

  return (
    <HeaderContainer>
      <HeaderWrapper>
        <LogoWrapper
          onClick={() => {
            dispatch(clearExploreCollections());
            dispatch(
              setSelectedExploreCollectionFilter(
                categories ? categories[0]?._id : "",
              ),
            );
            navigate("/");

            window.scrollTo({
              top: 0,
              left: 0,
              behavior: "instant",
            });
          }}
        >
          <Logo height={40} />
        </LogoWrapper>
        <HeaderRightSide>
          {user !== null ? (
            <ProUpgradeButtonContainer onClick={handleUpgradeToPro}>
              <StarSvg />
              <CreditText>
                {user !== undefined ? priceFormatter(user.credits) : 0}
              </CreditText>
            </ProUpgradeButtonContainer>
          ) : (
            <ProUpgradeButtonSkeleton $animationTime={5} />
          )}

          <ProfileSection ref={dropdownRef}>
            {isProfilePhotoLoading ? (
              <HeaderProfilePhotoSkeleton $animationTime={1.5} />
            ) : user ? (
              user.profilePhoto ? (
                <HeaderProfilePhoto
                  src={user.profilePhoto}
                  alt={`${user.username || "User"} profile photo`}
                  onClick={toggleDropdown}
                />
              ) : (
                <DefaultProfilePhoto
                  width={32}
                  height={32}
                  onClick={toggleDropdown}
                  style={{
                    cursor: "pointer",
                    border: "1px solid #4d8dff",
                    borderRadius: "50%",
                    boxShadow:
                      "0 0 9.5px rgba(0, 91, 255, 0.42), 0 -1px 9.5px 0 #005bff inset",
                  }}
                  header
                />
              )
            ) : (
              <DefaultProfilePhoto
                width={32}
                height={32}
                onClick={toggleDropdown}
                style={{
                  borderRadius: "50%",
                  cursor: "pointer",
                  border: "1px solid #4d8dff",
                  boxShadow:
                    "0 0 9.5px rgba(0, 91, 255, 0.42), 0 -1px 9.5px 0 #005bff inset",
                }}
                header
              />
            )}

            {isDropdownOpen && (
              <DropdownMenu $animating={isDropdownAnimating}>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("subscriptions")}
                >
                  Subscription <WhiteStarSvg width={18} height={18} />
                </DropdownItem>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("freeCredits")}
                >
                  Free Credits <ParticlesSvg width={18} height={18} />
                </DropdownItem>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("affiliate")}
                >
                  Affiliate <AffiliateSvg width={18} height={18} />
                </DropdownItem>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("templateRewards")}
                >
                  Template Rewards <RewardsSvg width={18} height={18} />
                </DropdownItem>

                <DropdownItem onClick={() => handleDropdownItemClick("wallet")}>
                  Wallet <WalletSvg width={18} height={18} />
                </DropdownItem>

                <DropdownSeparator />

                <DropdownItem
                  onClick={() => handleDropdownItemClick("autoPlay")}
                >
                  AutoPlay
                  {autoplayEnabled ? <PlayIcon /> : <PauseIcon />}
                </DropdownItem>
                <DropdownItem onClick={() => handleDropdownItemClick("logout")}>
                  Logout <LogoutSvg width={18} height={18} />
                </DropdownItem>
                {/* <DropdownItem
                  onClick={() => handleDropdownItemClick("deleteAccount")}
                >
                  Delete Account{" "}
                  <DeleteSvg
                    width={18}
                    height={18}
                    style={{ marginRight: 3 }}
                  />
                </DropdownItem> */}

                <DropdownSeparator />
                <Row onClick={handleProfileRowClick}>
                  <NameColumn>
                    <EmailText>{user?.username}</EmailText>
                  </NameColumn>
                  {shouldOpenUsernameModalFromProfileRow ? (
                    <EditSvg width={18} height={18} />
                  ) : (
                    <ProfileSvg />
                  )}
                </Row>
              </DropdownMenu>
            )}
          </ProfileSection>
        </HeaderRightSide>

        <FreeCreditsModal
          isOpen={isFreeCreditsModalOpen}
          onClose={handleFreeCreditsModalClose}
        />

        <AuthModal
          isOpen={isLoginModalOpen}
          onClose={() => {
            dispatch(setSignUpModalOpen(false));
            setIsLoginModalOpen(false);
          }}
        />

        <UsernameModal
          isOpen={isUsernameModalOpen}
          onClose={() => setIsUsernameModalOpen(false)}
        />
      </HeaderWrapper>
    </HeaderContainer>
  );
};

export default Header;
